import { without } from 'lodash';
import { App, View } from '@gin-betting/core';

import type { ScoreConfig, GetMetaPart, SetMatchPart } from './types';
import { LIVE_SPORTEVENT_STATUSES } from 'app-constants';

const emptyMeta: View.Meta = { name: '', value: '', id: '' };

export const getMetaPartIceHockeyFootball: GetMetaPart = (meta, sportId) => {
    const config = CONFIG_BY_SPORTS[sportId];

    const period = createPeriod(meta);

    if (period && config?.timeSort?.[period.name]) return period;

    return emptyMeta;
};

export function makeCreateMetaByType([type, typeNumber]: [
    App.MetaTypes,
    App.MetaTypes
]) {
    return (meta: View.Meta[]): View.Meta | undefined => {
        const metaType = meta.find(({ name }) => name === type)?.value;
        const metaTypeValue = meta.find(({ name }) => name === typeNumber)
            ?.value;

        if (!metaType || !metaTypeValue) return;

        return {
            name: metaType,
            value: metaTypeValue,
        };
    };
}

export const createPeriod = makeCreateMetaByType([
    App.MetaTypes.TYPE_PERIOD,
    App.MetaTypes.TYPE_PERIOD_NUMBER,
]);

export const createGameState = makeCreateMetaByType([
    App.MetaTypes.TYPE_GAME_STATE,
    App.MetaTypes.TYPE_STATE_NUMBER,
]);

export const getMetaPartEsportsFifa: GetMetaPart = (meta, sportId) => {
    const config = CONFIG_BY_SPORTS[sportId];

    const period = createPeriod(meta);

    if (period && config?.timeSort?.[period.name]) return period;

    if (period && config?.insteadMeta?.includes(period.name as App.MetaTypes)) {
        return {
            name: App.MetaTypes.TYPE_PERIOD_2ND_HALF,
            value: '',
        };
    }

    return emptyMeta;
};

export const getMetaPartEsportsIceHockey: GetMetaPart = (meta, sportId) => {
    const config = CONFIG_BY_SPORTS[sportId];

    const period = createPeriod(meta);

    if (period && config?.timeSort?.[period.name]) return period;

    if (period && config?.insteadMeta?.includes(period.name as App.MetaTypes)) {
        return { name: App.MetaTypes.TYPE_REGULAR, value: '3' };
    }

    return emptyMeta;
};

export const getMetaPartEsportsRocketLeagueCounterStrikeRainbowSix: GetMetaPart = (
    meta,
    sportId
) => {
    const config = CONFIG_BY_SPORTS[sportId];

    const gameState = createGameState(meta);

    if (gameState && config?.timeSort?.[gameState.name]) {
        return gameState;
    }

    return emptyMeta;
};

const tennisSetMatchPartTypes: SetMatchPart = (score, currentMatchPart) => {
    const isNumerable = App.ScoreTypesNumerable.includes(score.type);

    const result = {
        scoreTypesTitleMapping: SCORE_TYPES_TITLE_MAPPING,
        isNumerable,
    };

    if (!currentMatchPart) return result;

    const { type, indexNumber, status } = currentMatchPart;

    const isCurrentPart =
        LIVE_SPORTEVENT_STATUSES.includes(status) &&
        score.type === (type as App.ScoreTypes | undefined) &&
        score.indexNumber === indexNumber;

    if (!isCurrentPart) return result;

    return {
        scoreTypesTitleMapping: {
            ...SCORE_TYPES_TITLE_MAPPING,
            [App.ScoreTypes.TYPE_SET]: 'GS',
        },
        isNumerable: false,
    };
};

export const SCORE_TYPES_TITLE_MAPPING = {
    [App.ScoreTypes.TYPE_TOTAL]: 'Total',
    [App.ScoreTypes.TYPE_GAME_POINTS]: 'GP',
    [App.ScoreTypes.TYPE_SET]: 'S',
    [App.ScoreTypes.TYPE_TIE_BREAK]: 'TBP',
    [App.ScoreTypes.TYPE_GOLDEN_SET]: 'GS',
    [App.ScoreTypes.TYPE_YELLOW_CARD]: 'YC',
    [App.ScoreTypes.TYPE_RED_CARD]: 'RC',
    [App.ScoreTypes.TYPE_YELLOW_RED_CARD]: 'YRC',
    [App.ScoreTypes.TYPE_PERIOD_1ST_HALF]: '1H',
    [App.ScoreTypes.TYPE_PERIOD_1ST_EXTRA]: '1E',
    [App.ScoreTypes.TYPE_PERIOD_2ND_HALF]: '2H',
    [App.ScoreTypes.TYPE_PERIOD_2ND_EXTRA]: '2E',
    [App.ScoreTypes.TYPE_PERIOD_PENALTIES]: 'P',
    [App.ScoreTypes.TYPE_QUARTER]: 'Q',
    [App.ScoreTypes.TYPE_QUARTER_OVERTIME]: 'OT',
    [App.ScoreTypes.TYPE_PERIOD_REGULAR]: 'P',
    [App.ScoreTypes.TYPE_PERIOD_OVERTIME]: 'OT',
    [App.ScoreTypes.TYPE_PERIOD_BULLETS]: 'B',
    [App.ScoreTypes.TYPE_GAME_KILLS]: 'K',
    [App.ScoreTypes.TYPE_GAME_PLAYERS]: 'Players',
    [App.ScoreTypes.TYPE_MAP]: 'Map',
    [App.ScoreTypes.TYPE_ROUND_OVERTIME]: 'OT',
    [App.ScoreTypes.TYPE_INNING_POINTS]: 'IN',
};

export const metaTypesNumerable = without(
    App.MetaTypesNumerable,
    App.MetaTypes.TYPE_OVERTIME,
    App.MetaTypes.TYPE_BULLETS
);

const handballScoreTitleMapping = {
    ...SCORE_TYPES_TITLE_MAPPING,
    [App.ScoreTypes.TYPE_PERIOD_1ST_EXTRA]: '1ET',
    [App.ScoreTypes.TYPE_PERIOD_2ND_EXTRA]: '2ET',
};

const handballMatchPart: SetMatchPart = score => {
    const isNumerable = App.ScoreTypesNumerable.includes(score.type);

    return {
        scoreTypesTitleMapping: handballScoreTitleMapping,
        isNumerable,
    };
};

export const CONFIG_BY_SPORTS: {
    [sportId: string]: ScoreConfig;
} = {
    ice_hockey: {
        scoresOrder: {
            [App.ScoreTypes.TYPE_TOTAL]: 1,
            [App.ScoreTypes.TYPE_PERIOD_REGULAR]: 2,
            [App.ScoreTypes.TYPE_PERIOD_BULLETS]: 3,
            [App.ScoreTypes.TYPE_PERIOD_OVERTIME]: 4,
        },
        timeSort: {
            [App.MetaTypes.TYPE_REGULAR]: 1,
            [App.MetaTypes.TYPE_OVERTIME]: 2,
            [App.MetaTypes.TYPE_BULLETS]: 3,
        },
        partStartsFromZero: [App.MetaTypes.TYPE_REGULAR],
        customMetaTypesNumerable: without(
            App.MetaTypesNumerable,
            App.MetaTypes.TYPE_BULLETS
        ),
        isTimeInSeconds: true,
        isTimeCountdown: true,
        getMetaPart: getMetaPartIceHockeyFootball,
    },
    volleyball: {
        scoresOrder: {
            [App.ScoreTypes.TYPE_TOTAL]: 1,
            [App.ScoreTypes.TYPE_SET]: 2,
            [App.ScoreTypes.TYPE_GOLDEN_SET]: 3,
        },
        partStartsFromZero: [App.MetaTypes.TYPE_SET],
        timeSort: {
            [App.MetaTypes.TYPE_SET]: 1,
            [App.MetaTypes.TYPE_GOLDEN_SET]: 2,
        },
    },
    basketball: {
        scoresOrder: {
            [App.ScoreTypes.TYPE_TOTAL]: 1,
            [App.ScoreTypes.TYPE_QUARTER]: 2,
            [App.ScoreTypes.TYPE_GAME_POINTS]: 3,
        },
        partStartsFromZero: [App.MetaTypes.TYPE_QUARTER],
        timeSort: {
            [App.MetaTypes.TYPE_QUARTER]: 1,
        },
        isTimeInSeconds: true,
        isTimeCountdown: true,
    },
    football: {
        scoresOrder: {
            [App.ScoreTypes.TYPE_TOTAL]: 1,
            [App.ScoreTypes.TYPE_PERIOD_1ST_HALF]: 2,
            [App.ScoreTypes.TYPE_PERIOD_2ND_HALF]: 3,
            [App.ScoreTypes.TYPE_PERIOD_1ST_EXTRA]: 4,
            [App.ScoreTypes.TYPE_PERIOD_2ND_EXTRA]: 5,
            [App.ScoreTypes.TYPE_PERIOD_PENALTIES]: 6,
        },

        timeSort: {
            [App.MetaTypes.TYPE_PERIOD_1ST_HALF]: 1,
            [App.MetaTypes.TYPE_PERIOD_2ND_HALF]: 2,
            [App.MetaTypes.TYPE_PERIOD_1ST_EXTRA]: 3,
            [App.MetaTypes.TYPE_PERIOD_2ND_EXTRA]: 4,
            [App.MetaTypes.TYPE_PERIOD_PENALTIES]: 5,
        },
        isTimeInSeconds: true,
        getMetaPart: getMetaPartIceHockeyFootball,
    },
    tennis: {
        scoresOrder: {
            [App.ScoreTypes.TYPE_TOTAL]: 1,
            [App.ScoreTypes.TYPE_SET]: 2,
            [App.ScoreTypes.TYPE_GAME_POINTS]: 3,
            [App.ScoreTypes.TYPE_TIE_BREAK]: 4,
        },
        partStartsFromZero: [App.MetaTypes.TYPE_SET],
        timeSort: {
            [App.MetaTypes.TYPE_SET]: 1,
        },
        setMatchPartTypes: tennisSetMatchPartTypes,
    },
    handball: {
        scoresOrder: {
            [App.ScoreTypes.TYPE_TOTAL]: 1,
            [App.ScoreTypes.TYPE_PERIOD_1ST_HALF]: 2,
            [App.ScoreTypes.TYPE_PERIOD_2ND_HALF]: 3,
            [App.ScoreTypes.TYPE_PERIOD_1ST_EXTRA]: 4,
            [App.ScoreTypes.TYPE_PERIOD_2ND_EXTRA]: 5,
        },
        timeSort: {
            [App.MetaTypes.TYPE_PERIOD_1ST_HALF]: 1,
            [App.MetaTypes.TYPE_PERIOD_2ND_HALF]: 2,
            [App.MetaTypes.TYPE_PERIOD_1ST_EXTRA]: 3,
            [App.MetaTypes.TYPE_PERIOD_2ND_EXTRA]: 4,
            [App.MetaTypes.TYPE_PERIOD_PENALTIES]: 5,
        },
        customMetaTypesNaming: {
            [App.MetaTypes.TYPE_PERIOD_1ST_EXTRA]: '1ET',
            [App.MetaTypes.TYPE_PERIOD_2ND_EXTRA]: '2ET',
        },
        isTimeInSeconds: true,
        customMetaTypesTimeDisabled: [App.MetaTypes.TYPE_PERIOD_PENALTIES],
        getMetaPart: getMetaPartIceHockeyFootball,
        setMatchPartTypes: handballMatchPart,
    },
    table_tennis: {
        scoresOrder: {
            [App.ScoreTypes.TYPE_TOTAL]: 1,
            [App.ScoreTypes.TYPE_SET]: 2,
            [App.ScoreTypes.TYPE_GAME_POINTS]: 3,
            [App.ScoreTypes.TYPE_TIE_BREAK]: 4,
        },
        timeSort: {
            [App.MetaTypes.TYPE_SET]: 1,
        },
        isTimeDisabled: true,
    },
    beach_volleyball: {
        scoresOrder: {
            [App.ScoreTypes.TYPE_TOTAL]: 1,
            [App.ScoreTypes.TYPE_SET]: 2,
            [App.ScoreTypes.TYPE_GOLDEN_SET]: 3,
        },
        timeSort: {
            [App.MetaTypes.TYPE_SET]: 1,
            [App.MetaTypes.TYPE_GOLDEN_SET]: 2,
        },
        isTimeDisabled: true,
    },
    badminton: {
        scoresOrder: {
            [App.ScoreTypes.TYPE_TOTAL]: 1,
            [App.ScoreTypes.TYPE_SET]: 2,
            [App.ScoreTypes.TYPE_GOLDEN_SET]: 3,
        },
        timeSort: {
            [App.MetaTypes.TYPE_SET]: 1,
            [App.MetaTypes.TYPE_GOLDEN_SET]: 2,
        },
        isTimeDisabled: true,
    },
    baseball: {
        scoresOrder: {
            [App.ScoreTypes.TYPE_TOTAL]: 1,
            [App.ScoreTypes.TYPE_INNING_POINTS]: 2,
        },
        timeSort: {
            [App.MetaTypes.TYPE_INNING]: 1,
        },
    },

    esports_dota_2: {
        scoresOrder: {
            [App.ScoreTypes.TYPE_TOTAL]: 1,
            [App.ScoreTypes.TYPE_MAP]: 2,
        },
        timeSort: {
            [App.MetaTypes.TYPE_MAP]: 1,
        },
        titleParser: (index, text) => `${text}${index}`,
        isTimeInSeconds: true,
    },
    esports_fifa: {
        scoresOrder: {
            [App.ScoreTypes.TYPE_TOTAL]: 1,
            [App.ScoreTypes.TYPE_PERIOD_1ST_HALF]: 2,
            [App.ScoreTypes.TYPE_PERIOD_2ND_HALF]: 3,
        },
        timeSort: {
            [App.MetaTypes.TYPE_PERIOD_1ST_HALF]: 1,
            [App.MetaTypes.TYPE_PERIOD_2ND_HALF]: 2,
            [App.MetaTypes.TYPE_PERIOD]: 5,
        },
        isTimeInSeconds: true,
        insteadMeta: [
            App.MetaTypes.TYPE_PERIOD_1ST_EXTRA,
            App.MetaTypes.TYPE_PERIOD_2ND_EXTRA,
            App.MetaTypes.TYPE_PERIOD_PENALTIES,
        ],
        getMetaPart: getMetaPartEsportsFifa,
    },
    esports_basketball: {
        scoresOrder: {
            [App.ScoreTypes.TYPE_TOTAL]: 1,
            [App.ScoreTypes.TYPE_QUARTER]: 2,
            [App.ScoreTypes.TYPE_QUARTER_OVERTIME]: 3,
        },
        timeSort: {
            [App.MetaTypes.TYPE_QUARTER]: 1,
            [App.MetaTypes.TYPE_OVERTIME]: 2,
        },
        isTimeInSeconds: true,
        isTimeCountdown: true,
        customMetaTypesNumerable: without(
            App.MetaTypesNumerable,
            App.MetaTypes.TYPE_BULLETS
        ),
    },
    esports_ice_hockey: {
        scoresOrder: {
            [App.ScoreTypes.TYPE_TOTAL]: 1,
            [App.ScoreTypes.TYPE_PERIOD_REGULAR]: 2,
        },
        timeSort: {
            [App.MetaTypes.TYPE_REGULAR]: 1,
        },
        isTimeInSeconds: true,
        isTimeCountdown: true,
        insteadMeta: [App.MetaTypes.TYPE_BULLETS, App.MetaTypes.TYPE_OVERTIME],
        getMetaPart: getMetaPartEsportsIceHockey,
    },
    esports_counter_strike: {
        scoresOrder: {
            [App.ScoreTypes.TYPE_TOTAL]: 1,
            [App.ScoreTypes.TYPE_MAP]: 2,
        },
        timeSort: {
            [App.MetaTypes.TYPE_MAP]: 1,
            [App.MetaTypes.TYPE_OVERTIME]: 2,
        },
        titleParser: (index, text) => `${text}${index}`,
        getMetaPart: getMetaPartEsportsRocketLeagueCounterStrikeRainbowSix,
    },
    esports_league_of_legends: {
        scoresOrder: {
            [App.ScoreTypes.TYPE_TOTAL]: 1,
            [App.ScoreTypes.TYPE_MAP]: 2,
        },
        timeSort: {
            [App.MetaTypes.TYPE_MAP]: 1,
        },
        titleParser: (index, text) => `${text}${index}`,
        isTimeInSeconds: true,
    },
    esports_valorant: {
        scoresOrder: {
            [App.ScoreTypes.TYPE_TOTAL]: 1,
            [App.ScoreTypes.TYPE_MAP]: 2,
        },
        timeSort: {
            [App.MetaTypes.TYPE_MAP]: 1,
        },
        titleParser: (index, text) => `${text}${index}`,
        isTimeDisabled: true,
    },
    esports_rainbow_six: {
        scoresOrder: {
            [App.ScoreTypes.TYPE_TOTAL]: 1,
            [App.ScoreTypes.TYPE_MAP]: 2,
        },
        timeSort: {
            [App.MetaTypes.TYPE_MAP]: 1,
            [App.MetaTypes.TYPE_OVERTIME]: 2,
        },
        titleParser: (index, text) => `${text}${index}`,
        getMetaPart: getMetaPartEsportsRocketLeagueCounterStrikeRainbowSix,
        isTimeDisabled: true,
    },
    esports_rocket_league: {
        scoresOrder: {
            [App.ScoreTypes.TYPE_TOTAL]: 1,
            [App.ScoreTypes.TYPE_MAP]: 2,
        },
        timeSort: {
            [App.MetaTypes.TYPE_MAP]: 1,
            [App.MetaTypes.TYPE_OVERTIME]: 2,
        },
        titleParser: (index, text) => `${text}${index}`,
        getMetaPart: getMetaPartEsportsRocketLeagueCounterStrikeRainbowSix,
        isTimeInSeconds: true,
        isTimeCountdown: true,
        isTimeAbsolute: true,
    },
    esports_tennis: {
        scoresOrder: {
            [App.ScoreTypes.TYPE_TOTAL]: 1,
            [App.ScoreTypes.TYPE_SET]: 2,
            [App.ScoreTypes.TYPE_GAME_POINTS]: 3,
            [App.ScoreTypes.TYPE_TIE_BREAK]: 4,
        },
        partStartsFromZero: [App.MetaTypes.TYPE_SET],
        timeSort: {
            [App.MetaTypes.TYPE_SET]: 1,
        },
        setMatchPartTypes: tennisSetMatchPartTypes,
    },
};
